import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAPI, generalAPI, uploadAPI } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import './PostJob.css';
import './ProfileEdit.css';

export default function ProfileEdit() {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');
  const [form, setForm] = useState({
    full_name: '', org_name: '', bio: '', location: '', phone: '',
    website: '', job_title: '', sport_category_id: '', job_role_id: '',
    years_of_experience: '', skills: '', languages: '', org_type: '',
    founded_year: '', employee_count: ''
  });
  const [experience, setExperience] = useState([]);
  const [newExp, setNewExp] = useState({ position: '', organization: '', start_date: '', end_date: '', is_current: false, description: '' });

  useEffect(() => {
    Promise.all([
      authAPI.getMe(),
      generalAPI.getCategories(),
      generalAPI.getRoles()
    ]).then(([me, cats, rls]) => {
      const d = me.data.data;
      setForm({
        full_name: d.full_name || '',
        org_name: d.org_name || '',
        bio: d.bio || '',
        location: d.location || '',
        phone: d.phone || '',
        website: d.website || '',
        job_title: d.job_title || '',
        sport_category_id: d.sport_category_id || '',
        job_role_id: d.job_role_id || '',
        years_of_experience: d.years_of_experience || '',
        skills: d.skills || '',
        languages: d.languages || '',
        org_type: d.org_type || '',
        founded_year: d.founded_year || '',
        employee_count: d.employee_count || ''
      });
      setExperience(d.experience || []);
      setCategories(cats.data.data || []);
      setRoles(rls.data.data || []);
    }).finally(() => setLoading(false));
  }, []);

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await authAPI.updateProfile(form);
      updateUser(res.data.data);
      toast.success('تم حفظ التغييرات');
    } catch (err) {
      toast.error(err.response?.data?.message || 'فشل الحفظ');
    } finally {
      setSaving(false);
    }
  };

  const handleAvatarUpload = async e => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append(user.user_type === 'organization' ? 'logo' : 'avatar', file);
    try {
      const endpoint = user.user_type === 'organization' ? uploadAPI.logo : uploadAPI.avatar;
      await endpoint(fd);
      toast.success('تم رفع الصورة');
    } catch {
      toast.error('فشل رفع الصورة');
    }
  };

  if (loading) return <div className="page-loading"><div className="spinner"></div></div>;

  return (
    <div className="post-page">
      <div className="post-page-header">
        <div className="post-header-content">
          <h1>تعديل الملف الشخصي</h1>
          <p>حدّث معلوماتك لتظهر بأفضل صورة أمام الجهات الرياضية</p>
        </div>
      </div>

      <div className="post-form-container">
        <div className="profile-edit-tabs">
          {['basic', 'experience', 'skills'].map(tab => (
            <button key={tab}
              className={`tab-btn ${activeTab === tab ? 'active' : ''}`}
              onClick={() => setActiveTab(tab)}>
              {tab === 'basic' ? 'المعلومات الأساسية' : tab === 'experience' ? 'الخبرات' : 'المهارات'}
            </button>
          ))}
        </div>

        {activeTab === 'basic' && (
          <div className="post-form">
            <div className="form-section">
              <h2 className="form-section-title">صورة الملف الشخصي</h2>
              <div className="avatar-upload-area">
                <label className="avatar-upload-label">
                  <input type="file" accept="image/*" onChange={handleAvatarUpload} hidden />
                  <div className="avatar-upload-btn">📷 رفع صورة جديدة</div>
                </label>
              </div>
            </div>

            <div className="form-section">
              <h2 className="form-section-title">البيانات الشخصية</h2>
              <div className="form-grid">
                {user.user_type === 'individual' ? (
                  <div className="form-group full-width">
                    <label className="form-label">الاسم الكامل</label>
                    <input type="text" name="full_name" className="form-input" value={form.full_name} onChange={handleChange} />
                  </div>
                ) : (
                  <div className="form-group full-width">
                    <label className="form-label">اسم المؤسسة</label>
                    <input type="text" name="org_name" className="form-input" value={form.org_name} onChange={handleChange} />
                  </div>
                )}
                <div className="form-group full-width">
                  <label className="form-label">نبذة شخصية</label>
                  <textarea name="bio" className="form-input" rows="4" value={form.bio} onChange={handleChange}
                    placeholder="اكتب نبذة مختصرة عن نفسك ومسيرتك المهنية..." />
                </div>
                <div className="form-group">
                  <label className="form-label">الموقع</label>
                  <input type="text" name="location" className="form-input" value={form.location} onChange={handleChange} placeholder="المدينة، الدولة" />
                </div>
                <div className="form-group">
                  <label className="form-label">رقم الجوال</label>
                  <input type="text" name="phone" className="form-input" value={form.phone} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label className="form-label">الموقع الإلكتروني</label>
                  <input type="url" name="website" className="form-input" value={form.website} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label className="form-label">التصنيف الرياضي</label>
                  <select name="sport_category_id" className="form-input" value={form.sport_category_id} onChange={handleChange}>
                    <option value="">اختر</option>
                    {categories.map(c => <option key={c.id} value={c.id}>{c.name_ar || c.name}</option>)}
                  </select>
                </div>
                {user.user_type === 'individual' && (
                  <>
                    <div className="form-group">
                      <label className="form-label">المسمى الوظيفي</label>
                      <input type="text" name="job_title" className="form-input" value={form.job_title} onChange={handleChange} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">سنوات الخبرة</label>
                      <input type="number" name="years_of_experience" className="form-input" value={form.years_of_experience} onChange={handleChange} min="0" />
                    </div>
                  </>
                )}
                {user.user_type === 'organization' && (
                  <>
                    <div className="form-group">
                      <label className="form-label">نوع المؤسسة</label>
                      <select name="org_type" className="form-input" value={form.org_type} onChange={handleChange}>
                        <option value="">اختر</option>
                        <option value="club">نادي رياضي</option>
                        <option value="academy">أكاديمية</option>
                        <option value="federation">اتحاد</option>
                        <option value="school">مدرسة / جامعة</option>
                        <option value="company">شركة</option>
                        <option value="government">جهة حكومية</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">سنة التأسيس</label>
                      <input type="number" name="founded_year" className="form-input" value={form.founded_year} onChange={handleChange} />
                    </div>
                  </>
                )}
              </div>
            </div>
            <div className="form-actions">
              <button type="button" className="btn btn-outline" onClick={() => navigate(-1)}>إلغاء</button>
              <button type="button" className="btn btn-primary btn-lg" onClick={handleSave} disabled={saving}>
                {saving ? 'جاري الحفظ...' : '💾 حفظ التغييرات'}
              </button>
            </div>
          </div>
        )}

        {activeTab === 'skills' && (
          <div className="post-form">
            <div className="form-section">
              <h2 className="form-section-title">المهارات واللغات</h2>
              <div className="form-grid">
                <div className="form-group full-width">
                  <label className="form-label">المهارات (مفصولة بفاصلة)</label>
                  <input type="text" name="skills" className="form-input" value={form.skills} onChange={handleChange}
                    placeholder="التدريب، التحليل، التخطيط..." />
                </div>
                <div className="form-group full-width">
                  <label className="form-label">اللغات (مفصولة بفاصلة)</label>
                  <input type="text" name="languages" className="form-input" value={form.languages} onChange={handleChange}
                    placeholder="العربية، الإنجليزية، الفرنسية..." />
                </div>
              </div>
            </div>
            <div className="form-actions">
              <button type="button" className="btn btn-primary btn-lg" onClick={handleSave} disabled={saving}>
                {saving ? 'جاري الحفظ...' : '💾 حفظ'}
              </button>
            </div>
          </div>
        )}

        {activeTab === 'experience' && (
          <div className="post-form">
            {experience.map((exp, i) => (
              <div key={i} className="form-section experience-item">
                <h3>{exp.position} — {exp.organization}</h3>
                <p className="exp-period">{exp.start_date} — {exp.is_current ? 'حتى الآن' : exp.end_date}</p>
              </div>
            ))}
            <div className="form-section">
              <h2 className="form-section-title">إضافة خبرة جديدة</h2>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">المسمى الوظيفي</label>
                  <input type="text" className="form-input" value={newExp.position}
                    onChange={e => setNewExp(p => ({ ...p, position: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">اسم الجهة</label>
                  <input type="text" className="form-input" value={newExp.organization}
                    onChange={e => setNewExp(p => ({ ...p, organization: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">تاريخ البداية</label>
                  <input type="date" className="form-input" value={newExp.start_date}
                    onChange={e => setNewExp(p => ({ ...p, start_date: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">تاريخ النهاية</label>
                  <input type="date" className="form-input" value={newExp.end_date}
                    onChange={e => setNewExp(p => ({ ...p, end_date: e.target.value }))} disabled={newExp.is_current} />
                </div>
                <div className="form-group full-width">
                  <label className="form-label">وصف المهام</label>
                  <textarea className="form-input" rows="3" value={newExp.description}
                    onChange={e => setNewExp(p => ({ ...p, description: e.target.value }))} />
                </div>
              </div>
              <div className="form-actions">
                <button type="button" className="btn btn-primary" onClick={async () => {
                  try {
                    const { profilesAPI: pAPI } = await import('../utils/api');
                    await pAPI.addExperience(newExp);
                    setExperience(prev => [...prev, newExp]);
                    setNewExp({ position: '', organization: '', start_date: '', end_date: '', is_current: false, description: '' });
                    toast.success('تمت الإضافة');
                  } catch { toast.error('فشل الإضافة'); }
                }}>
                  ➕ إضافة
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
